<template>
  <v-card dark class="mx-auto py-3">
    <v-tabs class="d-flex justify-center" v-model="tab" dark background-color="transparent">
      <v-tab>Zeiterfassung</v-tab>
      <v-tab>
        <v-badge color="orange" :content="requestCount" :value="requestCount > 0" overlap>
          <span>Anträge & Wünsche</span>
        </v-badge>
      </v-tab>
      <v-tab>
        <v-badge color="amber darken-3" :content="pendingAbsenceCount" :value="pendingAbsenceCount > 0" overlap>
          <span>Abwesenheit</span>
        </v-badge>
      </v-tab>
      <v-tab>Übersicht</v-tab>
      <v-tab v-if="userService.hasRight('tt_eventsAdmin')">Übersicht Centerleiter</v-tab>
      <v-tab v-if="userService.hasRight('tt_eventsAdmin') && false">Übersicht Techniker</v-tab>
    </v-tabs>

    <v-divider class="mx-4 mb-4" style="border-color: rgba(255, 255, 255, 0.05)"></v-divider>

    <v-tabs-items dark v-model="tab" class="transparent-bg">
      <!-- TAB 1: Zeiterfassung -->
      <v-tab-item>
        <v-row v-if="loading">
          <v-col cols="12">
            <v-progress-linear indeterminate color="primary"></v-progress-linear>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12">
            <v-expansion-panels class="px-5" v-model="panel">
              <v-expansion-panel v-for="(employee, index) of filteredItems" :key="employee.user + '-panel'">
                <v-expansion-panel-header
                  class="employee-panel-header"
                  :class="{
                    'orange--text': submissionState(employee) && submissionState(employee).state === 'submitted',
                    'primary--text': submissionState(employee) && submissionState(employee).state === 'accepted',
                    'red--text': submissionState(employee) && submissionState(employee).state === 'rejected',
                  }">
                  <div class="employee-panel-header__content">
                    <div class="employee-panel-header__person" :class="{ 'font-weight-bold': submissionState(employee) }">
                      <span class="employee-panel-header__name">{{ employee.fullName }}</span>
                      <span v-if="employee.email" class="employee-panel-header__email">{{ employee.email }}</span>
                    </div>

                    <div class="employee-panel-header__states">
                      <span v-if="pendingShiftChangeCount(employee) > 0" class="pending-shift-change-status" role="status">
                        <span class="pending-shift-change-status__dot"></span>
                        <strong>{{ pendingShiftChangeCount(employee) }}</strong>
                        <span>{{ pendingShiftChangeCount(employee) === 1 ? "Änderung prüfen" : "Änderungen prüfen" }}</span>
                      </span>

                      <v-icon
                        v-if="
                          employee &&
                          employee.selfSubmittedTimetrackerMonths &&
                          employee.selfSubmittedTimetrackerMonths.find((month) => month === $moment(datePickerRange[0]).format('YYYY-MM'))
                        "
                        color="primary"
                        title="Vom Mitarbeiter eingereicht">
                        mdi-check-decagram
                      </v-icon>

                      <v-chip
                        v-if="submissionState(employee) && submissionState(employee).state !== 'onlyNotes'"
                        small
                        :color="
                          submissionState(employee) && submissionState(employee).state === 'rejected'
                            ? 'red'
                            : submissionState(employee) && submissionState(employee).state === 'accepted'
                            ? 'primary'
                            : 'orange'
                        ">
                        {{
                          submissionState(employee) && submissionState(employee).state === "rejected"
                            ? "abgelehnt"
                            : submissionState(employee) && submissionState(employee).state === "accepted"
                            ? "abgeschlossen"
                            : "eingereicht"
                        }}
                      </v-chip>
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <office-time-tracker-view
                    v-if="index === panel"
                    :studio="selectedStudioSingle"
                    @fetchEntries="trackerViewUpdated"
                    :date-range="datePickerRange"
                    :month="$moment(datePickerRange[0])"
                    :timeEntryView="employee"
                    :shift-change-history="shiftChangeHistoryForEmployee(employee)"
                    :shift-change-history-pending-count="pendingShiftChangeCount(employee)"
                    :shift-change-history-loaded="shiftChangeHistoryLoaded"
                    :shift-change-history-loading="shiftChangeHistoryLoading"
                    :shift-change-history-error="shiftChangeHistoryError"
                    @shift-changes-reviewed="handleShiftChangesReviewed"></office-time-tracker-view>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
          <v-col cols="12" v-if="Object.keys(filteredItems).length === 0">
            <v-alert class="mx-5" type="info">Es wurden keine Einträge gefunden</v-alert>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 2: Anträge & Wünsche (NEU) -->
      <v-tab-item>
        <v-container fluid class="px-4">
          <management-requests-view
            ref="requestsView"
            :selectedStudioSingle="selectedStudioSingle"
            @updateCount="updateRequestCount"></management-requests-view>
        </v-container>
      </v-tab-item>

      <!-- TAB 3: Abwesenheit -->
      <v-tab-item>
        <v-row v-if="tab === 2">
          <v-col cols="12">
            <sick-days-view
              :selectedStudioSingle="selectedStudioSingle"
              @fetchEntries="fetchEntries"
              @updateCount="updateRequestCount"></sick-days-view>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 4: Übersicht -->
      <v-tab-item>
        <v-row v-if="tab === 3">
          <v-col cols="12">
            <holiday-entries-view :datePickerRange="datePickerRange" :selectedStudioSingle="selectedStudioSingle"></holiday-entries-view>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 5: Übersicht Centerleiter -->
      <v-tab-item>
        <v-row v-if="tab === 4">
          <v-col cols="12">
            <holiday-entries-view :datePickerRange="datePickerRange" :studioManager="true"></holiday-entries-view>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- TAB 6: Übersicht Techniker (Optional) -->
      <v-tab-item>
        <v-row v-if="tab === 5">
          <v-col cols="12">
            <technic-team-overview :datePickerRange="datePickerRange"></technic-team-overview>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import OfficeTimeTrackerView from "./OfficeTimeTrackerView.vue"
import HolidayEntriesView from "./HolidayEntriesView.vue"
import TechnicTeamOverview from "./TechnicTeamOverview.vue"
import SickDaysView from "./SickDaysView.vue"
import ManagementRequestsView from "./ManagementRequestsView.vue"

export default {
  name: "ManagementTimeTracker",
  components: {
    OfficeTimeTrackerView,
    HolidayEntriesView,
    SickDaysView,
    TechnicTeamOverview,
    ManagementRequestsView,
  },
  props: {
    datePickerRange: {},
    selectedStudioSingle: {
      required: true,
    },
  },
  data() {
    return {
      tab: null,
      userService,
      panel: null,
      loading: false,
      filteredItems: [],
      shiftChangeHistoryEntries: [],
      shiftChangeHistorySummary: { byEmployee: {} },
      shiftChangeHistoryLoading: false,
      shiftChangeHistoryLoaded: false,
      shiftChangeHistoryError: "",
      shiftChangeHistoryRequest: null,
      shiftChangeHistoryRequestKey: "",
      requestCount: 0,
      pendingAbsenceCount: 0,
    }
  },
  // created() entfernt, da die Watchers mit immediate: true das übernehmen
  methods: {
    submissionState(employee) {
      if (
        employee.timeTrackerMonths &&
        employee.timeTrackerMonths.find((ttm) => ttm.month === this.$moment(this.datePickerRange[0]).format("YYYY-MM"))
      ) {
        return employee.timeTrackerMonths.find((ttm) => ttm.month === this.$moment(this.datePickerRange[0]).format("YYYY-MM"))
      }
      return
    },
    trackerViewUpdated(data) {
      this.fetchEntries(data)
      this.updateRequestCount()
    },
    normalizedId(value) {
      if (!value) return ""
      if (typeof value === "object") {
        if (value._id) return String(value._id)
        if (value.id) return String(value.id)
        if (value.user) return this.normalizedId(value.user)
      }
      return String(value)
    },
    employeeUserId(employee) {
      return this.normalizedId(employee && (employee.user || employee.employee || employee._id))
    },
    normalizedEmployeeName(value) {
      return String(value || "")
        .trim()
        .replace(/\s+/g, " ")
        .toLocaleLowerCase("de-CH")
    },
    employeeDisplayName(employee) {
      return this.normalizedEmployeeName(employee && (employee.fullName || employee.name))
    },
    requiresShiftChangeReview(employee) {
      return employee?.requiresShiftChangeReview !== false
    },
    shiftChangeEmployeeId(entry) {
      return this.normalizedId(
        entry && (entry.affectedEmployeeId || entry.affectedEmployee || entry.employeeId || entry.employee || entry.assignedEmployee),
      )
    },
    shiftChangeEmployeeName(entry) {
      const affectedEmployee = entry && entry.affectedEmployee
      const populatedName =
        affectedEmployee && typeof affectedEmployee === "object"
          ? affectedEmployee.fullName || [affectedEmployee.vorname, affectedEmployee.nachname].filter(Boolean).join(" ")
          : ""
      return this.normalizedEmployeeName(populatedName || (entry && entry.affectedEmployeeName))
    },
    shiftChangeHistoryForEmployee(employee) {
      if (!this.requiresShiftChangeReview(employee)) return []
      const employeeId = this.employeeUserId(employee)
      const employeeName = this.employeeDisplayName(employee)
      const employeesWithSameName = employeeName
        ? this.filteredItems.filter((candidate) => this.employeeDisplayName(candidate) === employeeName).length
        : 0

      return this.shiftChangeHistoryEntries.filter((entry) => {
        const entryEmployeeId = this.shiftChangeEmployeeId(entry)
        if (employeeId && entryEmployeeId === employeeId) return true

        // Compatibility for older/broken populated records: only use a name
        // fallback when that name identifies exactly one employee in the list.
        const entryEmployeeName = this.shiftChangeEmployeeName(entry)
        return Boolean(employeeName && employeesWithSameName === 1 && entryEmployeeName === employeeName)
      })
    },
    pendingShiftChangeCount(employee) {
      if (!this.requiresShiftChangeReview(employee)) return 0
      const employeeId = this.employeeUserId(employee)
      const summaryEntry = employeeId && this.shiftChangeHistorySummary.byEmployee && this.shiftChangeHistorySummary.byEmployee[employeeId]
      if (summaryEntry && Number.isFinite(Number(summaryEntry.pending))) return Number(summaryEntry.pending)
      return this.shiftChangeHistoryForEmployee(employee).filter((entry) => entry.reviewStatus === "pending").length
    },
    async fetchShiftChangeHistory() {
      const hasReviewEmployees = Array.isArray(this.filteredItems) && this.filteredItems.some((employee) => this.requiresShiftChangeReview(employee))
      if (!this.selectedStudioSingle || !this.datePickerRange || !this.datePickerRange[0] || !hasReviewEmployees) {
        this.shiftChangeHistoryEntries = []
        this.shiftChangeHistorySummary = { byEmployee: {} }
        this.shiftChangeHistoryLoaded = false
        this.shiftChangeHistoryLoading = false
        this.shiftChangeHistoryError = ""
        this.shiftChangeHistoryRequestKey = ""
        this.shiftChangeHistoryRequest = null
        return
      }

      const startDate = this.$moment(this.datePickerRange[0]).format("YYYY-MM-DD")
      const endDate = this.datePickerRange[1]
        ? this.$moment(this.datePickerRange[1]).format("YYYY-MM-DD")
        : this.$moment(this.datePickerRange[0]).endOf("month").format("YYYY-MM-DD")
      const studioId = this.selectedStudioSingle._id || this.selectedStudioSingle
      const requestKey = [studioId, startDate, endDate, "tenant"].join("|")

      if (this.shiftChangeHistoryLoading && this.shiftChangeHistoryRequestKey === requestKey && this.shiftChangeHistoryRequest) {
        return this.shiftChangeHistoryRequest
      }

      this.shiftChangeHistoryLoading = true
      this.shiftChangeHistoryLoaded = false
      this.shiftChangeHistoryError = ""
      this.shiftChangeHistoryEntries = []
      this.shiftChangeHistorySummary = { byEmployee: {} }
      this.shiftChangeHistoryRequestKey = requestKey
      const request = api.fetchShiftChangeHistory({ studioId, startDate, endDate, source: "employee_self", scope: "tenant" })
      this.shiftChangeHistoryRequest = request
      try {
        const result = await request
        if (this.shiftChangeHistoryRequestKey !== requestKey || this.shiftChangeHistoryRequest !== request) return result
        this.shiftChangeHistoryEntries = Array.isArray(result) ? result : Array.isArray(result && result.entries) ? result.entries : []
        this.shiftChangeHistorySummary = result && result.summary ? result.summary : { byEmployee: {} }
        this.shiftChangeHistoryLoaded = true
      } catch (error) {
        if (this.shiftChangeHistoryRequestKey === requestKey && this.shiftChangeHistoryRequest === request) {
          console.error("Error loading shift change history:", error)
          this.shiftChangeHistoryEntries = []
          this.shiftChangeHistorySummary = { byEmployee: {} }
          this.shiftChangeHistoryLoaded = false
          this.shiftChangeHistoryError = error?.response?.data?.message || "Schichtänderungen konnten nicht geladen werden."
        }
      } finally {
        if (this.shiftChangeHistoryRequestKey === requestKey && this.shiftChangeHistoryRequest === request) {
          this.shiftChangeHistoryLoading = false
          this.shiftChangeHistoryRequest = null
        }
      }
    },
    async handleShiftChangesReviewed() {
      await this.fetchShiftChangeHistory()
    },
    async fetchEntries(data = null) {
      // WICHTIG: Abbrechen, wenn Daten noch nicht da sind, um Fehler zu vermeiden
      if (!this.selectedStudioSingle || !this.datePickerRange || !this.datePickerRange[0]) {
        return
      }

      this.loading = true
      try {
        this.filteredItems = await api.fetchOfficeTimeEntriesForStudio(this.datePickerRange, this.selectedStudioSingle)
      } catch (e) {
        this.filteredItems = []
        this.$toast.error("Fehler beim Laden der Einträge")
      } finally {
        this.loading = false
      }
      await this.fetchShiftChangeHistory()
    },
    async updateRequestCount(count = null) {
      if (!this.selectedStudioSingle) {
        this.requestCount = 0
        this.pendingAbsenceCount = 0
        return
      }
      if (count !== null && typeof count === "number") {
        this.requestCount = count
      }
      try {
        // If count is not passed, fetch the open requests count
        if (count === null || typeof count !== "number") {
          const startDate = this.$moment().startOf("year").format("YYYY-MM-DD")
          const endDate = this.$moment().add(3, "months").endOf("month").format("YYYY-MM-DD")
          const openReqs = await api.fetchManagementRequests(this.selectedStudioSingle, "open", startDate, endDate)
          this.requestCount = openReqs.length
        }

        // Fetch all sick entries to count the pending_cl (waiting for CL) ones
        const sickEntries = await api.fetchSickEntriesManagement(this.selectedStudioSingle)
        this.pendingAbsenceCount = sickEntries.filter((e) => e.status === "pending_cl").length
      } catch (e) {
        console.error("Error updating requests count:", e)
      }
    },
  },
  watch: {
    datePickerRange: {
      handler() {
        // Check ob Range gültig ist
        if (this.datePickerRange && this.datePickerRange[0]) {
          this.fetchEntries()
          this.updateRequestCount()
        }
      },
      deep: true,
      immediate: true, // FIX: Feuert sofort beim Laden, wenn Daten da sind
    },
    selectedStudioSingle: {
      handler() {
        if (this.selectedStudioSingle) {
          this.fetchEntries()
          this.updateRequestCount()
        }
      },
      immediate: true, // FIX: Feuert sofort beim Laden, wenn Daten da sind
    },
  },
}
</script>

<style lang="scss" scoped>
.transparent-bg {
  background: transparent !important;
}

.employee-panel-header__content {
  display: flex;
  min-width: 0;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.employee-panel-header__person {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 8px;
}

.employee-panel-header__name {
  color: rgba(255, 255, 255, 0.94);
  font-weight: 700;
}

.employee-panel-header__email {
  overflow: hidden;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.employee-panel-header__states {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.pending-shift-change-status {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  padding: 2px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
}

.pending-shift-change-status strong {
  color: #ffab91;
}

.pending-shift-change-status__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ff7043;
  box-shadow: 0 0 0 3px rgba(255, 112, 67, 0.14);
}

@media (max-width: 700px) {
  .employee-panel-header__content {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .employee-panel-header__states {
    flex-wrap: wrap;
  }
}
</style>
